import React, { useMemo, useState } from "react";
import "./Cart.css";
import { useCart } from "../../context/CartContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const Cart = ({ onClose }) => {
  const navigate = useNavigate();
  const { cartItems, updateCart } = useCart();
  const { isMember } = useAuth();
  const [limitDialog, setLimitDialog] = useState("");

  const uiCartItems = useMemo(() => {
    return cartItems.map((item) => {
      if (item.type === "product") {
        return {
          type: "product",
          id: item.product.id,
          name: item.product.name,
          image: item.product.image,
          price: Number(item.product.price),
          qty: item.quantity,
          brand: item.product.category?.name || "",
        };
      }

      if (item.type === "preorder") {
        return {
          type: "preorder",
          id: item.preorder_product.id,
          name: item.preorder_product.name,
          image: item.preorder_product.image,
          price: Number(item.preorder_product.deposit_amount),
          tax: Number(item.preorder_product.product_tax || 0),
          qty: item.quantity,
          brand: item.preorder_product.category?.name || "Pre-Order",
          maxUnitPerUser: Number(item.preorder_product.max_unit_per_user) > 0
            ? Number(item.preorder_product.max_unit_per_user)
            : null,
        };
      }

      if (item.type === "clearance") {
        const clearanceProduct = item.clearance_product || item.product || {};

        return {
          type: "clearance",
          id: clearanceProduct.id,
          name: clearanceProduct.name,
          image: clearanceProduct.image_url || clearanceProduct.image,
          price: Number(clearanceProduct.selling_price),
          tax: Number(clearanceProduct.product_tax || 0),
          qty: item.quantity,
          brand: "Clearance Sale",
        };
      }

      return {
        type: "combo",
        id: item.combo.id,
        name: item.combo.name,
        image: item.combo.image,
        price: Number(item.combo.price),
        tax: Number(item.combo.product_tax || 0),
        qty: item.quantity,
        brand: "Combo Offer",
      };
    });
  }, [cartItems]);

  /* -------------------- TOTALS -------------------- */
  const subtotal = useMemo(() => {
    return uiCartItems.reduce(
      (sum, item) =>
        sum +
        ((isMember && item.type === "preorder"
          ? 0
          : item.price + (item.tax || 0)) *
          item.qty),
      0
    );
  }, [isMember, uiCartItems]);
  const formattedSubtotal = subtotal.toFixed(2);

  /* -------------------- ACTIONS -------------------- */
  const increaseQty = (item) => {
    if (
      item.type === "preorder" &&
      item.maxUnitPerUser &&
      item.qty >= item.maxUnitPerUser
    ) {
      setLimitDialog(
        `You can only add up to ${item.maxUnitPerUser} unit${
          item.maxUnitPerUser > 1 ? "s" : ""
        } of ${item.name} per user.`
      );
      return;
    }

    updateCart({
      type: item.type,
      id: item.id,
      delta: 1,
    });
  };

  const decreaseQty = (item) => {
    updateCart({
      type: item.type,
      id: item.id,
      delta: -1,
    });
  };

  const removeItem = (item) => {
    updateCart({
      type: item.type,
      id: item.id,
      delta: -item.qty,
    });
  };

  const goToCheckout = () => {
    onClose();
    navigate("/checkout");
  };

  /* -------------------- RENDER -------------------- */
  return (
    <div className="cart-overlay" onClick={onClose}>
      {limitDialog ? (
        <div className="checkout-dialog-backdrop" onClick={() => setLimitDialog("")}>
          <div
            className="checkout-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Quantity Limit Reached</h3>
            <p>{limitDialog}</p>
            <div className="checkout-dialog-actions">
              <button
                className="checkout-btn-secondary"
                onClick={() => setLimitDialog("")}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div
        className="cart-popup"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="cart-header">
          <h3>SHOPPING BAG</h3>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* EMPTY STATE */}
        {uiCartItems.length === 0 && (
          <div className="cart-empty">
            <div className="cart-empty-icon">🛍️</div>
            <h4>Your bag is empty</h4>
            <p>
              Looks like you haven’t added anything yet.
              <br />
              Let’s find something you’ll love.
            </p>

            <button
              className="cart-empty-btn"
              onClick={onClose}
            >
              Continue Shopping
            </button>
          </div>
        )}

        {/* ITEMS */}
        {uiCartItems.length > 0 && (
          <>
            <div className="cart-message">
              Item successfully added to your shopping bag
            </div>

            {uiCartItems.map((item) => (
              <div
                className="cart-item"
                key={`${item.type}-${item.id}`}
              >
                {(() => {
                  const displayLinePrice =
                    isMember && item.type === "preorder"
                      ? "₹0.00"
                      : `₹${item.price * item.qty}`;

                  return (
                    <>
                <img
                  src={item.image}
                  alt={item.name}
                />

                <div className="item-details">
                  <h4>{item.name}</h4>
                  <p>{item.brand}</p>

                  <div className="qty-price">
                    <div className="cart-price">
                      {displayLinePrice}
                    </div>

                    <div className="qty-box">
                      <button
                        onClick={() => decreaseQty(item)}
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span>{item.qty}</span>
                      <button
                        onClick={() => increaseQty(item)}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  className="remove-item-btn"
                  onClick={() => removeItem(item)}
                  aria-label="Remove item"
                >
                  ×
                </button>
                    </>
                  );
                })()}
              </div>
            ))}

            {/* SUMMARY */}
            <div className="cart-summary">
              <div className="row">
                <span>Subtotal</span>
                <span>₹{formattedSubtotal}</span>
              </div>

              <div className="row info">
                <span>Shipping</span>
                <span>—</span>
              </div>

              <div className="row info">
                <span>Taxes</span>
                <span>—</span>
              </div>

              <div className="row total">
                <span>Total</span>
                <span>₹{formattedSubtotal}</span>
              </div>
            </div>

            <button
              className="checkout-btn"
              onClick={goToCheckout}
            >
              CONTINUE TO CHECKOUT
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Cart;
